import type { MessageTransport } from '@speles7172/message-console';
import type { Channel, TemplateView } from '@speles7172/message-client/core';
import { apiClient } from './client';
import { messageTransport } from './notifications';

/**
 * Settings → Messaging: every email and notification the application sends,
 * through `/admin/message-templates` (ADMIN).
 *
 * The transport is the notifications one with the administrative capabilities
 * added — `<TemplatesConsole>` asks it for `templates`, `saveTemplate`,
 * `resetTemplate`, and offers its on/off switch and "Send a test to me" only
 * because `setTemplateEnabled` and `sendTestTemplate` are present. ⚠️ **A module constant**, for the reason `api/notifications`
 * gives: the console keys its effects on the transport's identity.
 */
const BASE = '/admin/message-templates';

interface TestSend {
  to: string;
  // `SUPPRESSED`: the administrator's own address bounced before; the reason says so.
  status: 'SENT' | 'SIMULATED' | 'FAILED' | 'SUPPRESSED';
  errorMessage: string | null;
}

const path = (event: string, channel: Channel) =>
  `${BASE}/${encodeURIComponent(event)}/${encodeURIComponent(channel)}`;

export const templatesTransport: MessageTransport = {
  ...messageTransport,

  async templates() {
    const { data } = await apiClient.get<TemplateView[]>(BASE);
    return data;
  },

  async saveTemplate({ event, channel, subject, body, enabled }) {
    const { data } = await apiClient.put<TemplateView>(path(event, channel), {
      subject,
      body,
      ...(enabled === undefined ? {} : { enabled }),
    });
    return data;
  },

  async resetTemplate({ event, channel }) {
    const { data } = await apiClient.delete<TemplateView>(path(event, channel));
    return data;
  },

  async setTemplateEnabled({ event, channel, enabled }) {
    const { data } = await apiClient.put<TemplateView>(`${path(event, channel)}/enabled`, {
      enabled,
    });
    return data;
  },

  /**
   * The draft on screen, emailed to the administrator. The endpoint answers
   * 200 with the delivery log whatever became of the message, and the console
   * has one way to report success — "Sent a test to …" — so anything short of
   * `SENT` is a rejection here: a refusal carries the mail service's reason,
   * and a simulated send (outside production nothing reaches SES) says so
   * rather than let an administrator wait for an email that was never sent.
   */
  async sendTestTemplate({ event, subject, body }) {
    const { data } = await apiClient.post<TestSend>(`${BASE}/${encodeURIComponent(event)}/test`, {
      subject,
      body,
    });
    if (data.status === 'SIMULATED') {
      throw new Error(
        `Nothing was sent to ${data.to}: email is simulated in this environment, so the test went no further than the log.`,
      );
    }
    if (data.status !== 'SENT') {
      throw new Error(data.errorMessage || `The test to ${data.to} could not be sent.`);
    }
    return { to: data.to };
  },
};
